import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Wallet } from "lucide-react";
import { money } from "@/lib/constants";

const METHODS = [
  { value: "efectivo", label: "Efectivo" },
  { value: "transferencia", label: "Transferencia" },
  { value: "tarjeta", label: "Tarjeta" },
  { value: "otro", label: "Otro" },
];

const STATUS_LABEL_ES = {
  pending: "Pendiente",
  partial: "Parcial",
  paid: "Pagada",
  cancelled: "Cancelada",
};

export function PaymentDialog({ open, onOpenChange, ap, onSuccess }) {
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("transferencia");
  const [methodNote, setMethodNote] = useState("");
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");
  const [date, setDate] = useState("");
  const [saving, setSaving] = useState(false);

  const balance = Number(ap?.balance_due || 0);

  useEffect(() => {
    if (open) {
      setAmount("");
      setMethod("transferencia");
      setMethodNote("");
      setReference("");
      setNotes("");
      setDate(new Date().toISOString().slice(0, 10));
    }
  }, [open, ap?.id]);

  function payAll() {
    setAmount(String(balance.toFixed(2)));
  }

  async function submit() {
    const n = Number(amount);
    if (!n || n <= 0) { toast.error("Ingresa un monto mayor a cero"); return; }
    if (n > balance + 0.009) { toast.error(`No puede exceder el saldo (${money(balance)})`); return; }
    if (method === "otro" && !methodNote.trim()) {
      toast.error("Describe el método en el campo de nota de método");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post(`/accounts-payable/${ap.id}/payments`, {
        amount: n,
        payment_method: method,
        method_note: method === "otro" ? methodNote.trim() : null,
        reference: reference.trim() || null,
        notes: notes.trim() || null,
        payment_date: date || null,
      });
      toast.success(
        `Pago registrado · ${money(n)} · nuevo saldo ${money(data.accounts_payable.balance_due)} · estatus ${STATUS_LABEL_ES[data.accounts_payable.status] || data.accounts_payable.status}`,
      );
      onOpenChange(false);
      onSuccess?.(data.accounts_payable);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="ap-payment-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wallet className="h-5 w-5" /> Registrar pago
          </DialogTitle>
          <DialogDescription>
            Saldo actual: <b className="mono">{money(balance)}</b> · Proveedor: {ap?.supplier_name}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2 col-span-2">
            <Label>Monto *</Label>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                min="0"
                step="0.01"
                max={balance}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                data-testid="ap-payment-amount"
              />
              <Button size="sm" variant="outline" type="button" onClick={payAll} data-testid="ap-payment-payall">
                Pagar todo
              </Button>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Fecha</Label>
            <Input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              data-testid="ap-payment-date"
            />
          </div>
          <div className="space-y-2">
            <Label>Método *</Label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger data-testid="ap-payment-method"><SelectValue /></SelectTrigger>
              <SelectContent>
                {METHODS.map((m) => (<SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          {method === "otro" && (
            <div className="space-y-2 col-span-2">
              <Label>Detalle del método *</Label>
              <Input
                value={methodNote}
                onChange={(e) => setMethodNote(e.target.value)}
                placeholder="Ej. Depósito en tienda, cheque, etc."
                data-testid="ap-payment-method-note"
              />
            </div>
          )}
          <div className="space-y-2 col-span-2">
            <Label>Referencia</Label>
            <Input
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="Ej. SPEI-1234, folio banco, cheque #…"
              data-testid="ap-payment-reference"
            />
          </div>
          <div className="space-y-2 col-span-2">
            <Label>Notas</Label>
            <Textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              data-testid="ap-payment-notes"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button className="btn-tenant" onClick={submit} disabled={saving || !amount} data-testid="ap-payment-submit">
            {saving ? "Guardando…" : "Registrar pago"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
